import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Checkbox,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
  Select,
  FormControlLabel,
  Switch,
  IconButton,
  Grid,
  TextField,
  Typography,
  Tooltip,
  OutlinedInput,
  Chip,
  Snackbar,
  AlertTitle,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MuiAlert from "@mui/material/Alert";
import AddIcon from "@mui/icons-material/Add";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { fetchData, postData } from "../../utils/api";
import { UserContext } from "../../App";
import ExperimentProcessingStatus from "./ExperimentProcessingStatus";
import ExperimentConfiguratorErrorModel from "./ExperimentConfiguratorErrorModel";
import ExperimentPreview from "./ExperimentPreview";
import { getSelectorStyles } from "../../utils/table-utils";

const Alert = forwardRef((props, ref) => (
  <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
));

function ViewToggle({
  checked,
  onChange,
  tooltip,
  checkedLabel,
  uncheckedLabel,
}) {
  const label = checked ? checkedLabel : uncheckedLabel;

  return (
    <Tooltip title={tooltip}>
      <FormControlLabel
        control={
          <Switch
            checked={checked}
            onChange={onChange}
            size="small"
            sx={{
              "& .MuiSwitch-track": {
                opacity: 1,
                backgroundColor: "rgba(0, 0, 0, 0.32)",
              },
              "& .MuiSwitch-switchBase": {
                color: "#fff",
              },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                opacity: 1,
                backgroundColor: "primary.main",
              },
            }}
          />
        }
        label={label}
        sx={{ marginRight: 0 }}
      />
    </Tooltip>
  );
}

export default function ExperimentConfigurator() {
  const theme = useTheme();
  // init internationalization
  const { t } = useTranslation();
  // needed for form validation
  const formRef = useRef();

  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  const navigate = useNavigate();

  // get type from URL query
  const experimentType = useQuery().get("type");

  // get data from current dataset
  const {
    datasets,
    currentDatasetIdx,
    setExperiments,
    setCurrentExperimentIdx,
  } = useContext(UserContext);

  const [baseData, setBaseData] = useState([""]);
  const [processingStarted, setProcessingStarted] = useState(false);
  const [columnNames, setColumnNames] = useState([""]);
  const [columnMapping, setColumnMapping] = useState({});
  const [experimentName, setExperimentName] = useState("");
  const [sameDistributionTargets, setSameDistributionTargets] = useState(false);
  const [errors, setErrors] = useState({});
  const [splitFiles, setSplitFiles] = useState([]);
  const [selectedSplitFile, setSelectedSplitFile] = useState("");
  const [splitMethod, setSplitMethod] = useState("");
  const [validationRatio, setValidationRatio] = useState("17");
  const [ValSplitIndex, setValSplitIndex] = useState("");
  const [useTargetListbox, setUseTargetListbox] = useState(false);
  const [useIgnoredListbox, setUseIgnoredListbox] = useState(false);

  const fetchSplitInfo = async () => {
    try {
      const res = await fetchData(
        `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasets[currentDatasetIdx].datasetName}/split-info`,
      );
      setValidationRatio(parseInt(res.splitDefaults.ValRatioDefault * 100, 10));
      setValSplitIndex(res.splitDefaults.ValIndexDefault);
      if (res.splitFiles && res.splitFiles.length > 0) {
        setSplitFiles(res.splitFiles);
        // Keep "default" as the selected option unless user changes it
        if (!selectedSplitFile || selectedSplitFile === "default") {
          setSelectedSplitFile("default");
        }
      } else {
        setSplitFiles([]);
        setSelectedSplitFile("default");
      }
    } catch (err) {
      console.error("Error fetching split files:", err);
      setSplitFiles([]);
      setSelectedSplitFile("default");
    }
  };

  const fetchDataset = () => {
    fetchData(
      `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasets[currentDatasetIdx].datasetName}?num_entries=10`,
    ).then((data) => {
      // set datasets and use first dataset as default
      setBaseData(data.baseData);
      setColumnNames(Object.keys(data.baseData));
      // create column Mapping, to give each column a purpose (feature, target, id etc.) The default purpose is "feature"
      const mapping = Object.keys(data.baseData).reduce((obj, name) => {
        obj[name] = { type: "feature" };
        return obj;
      }, {});
      setColumnMapping(mapping);
    });
  };

  // Error Snackbars
  const [snackbarSettings, setSnackbarSettings] = useState({
    isOpen: false,
    type: "info",
    message: "",
  });

  // Error Alert
  const [alertSettings, setAlertSettings] = useState({
    isVisible: false,
    type: "error",
    message: "",
  });

  function getKeysArray(columnTypeName) {
    /**
     * Helper Method, that uses the column mapping and returns an array list of keys by a given col type
     * e.g. if you want all "feature" columns, the value would be "feature"
     * */
    return Object.entries(columnMapping)
      .filter(([, value]) => value.type === columnTypeName)
      .map(([key]) => key);
  }
  const targetHelperText = (() => {
    if (errors.target?.message) {
      return t(`experimentConfigurator.Error ${errors.target.message}`);
    }

    if (experimentType !== "Classification") {
      return `${t("experimentConfigurator.TargetDropdown")} selected: ${
        getKeysArray("target").length
      }`;
    }

    return " ";
  })();

  const handleNativeMultiSelectorChange = (selectorName) => (event) => {
    // reset error message
    if (errors.hasOwnProperty(selectorName)) {
      const errorsCopy = { ...errors };
      delete errorsCopy[selectorName];
      setErrors(errorsCopy);
    }

    const selectedValues = Array.from(event.target.selectedOptions).map(
      (option) => option.value,
    );
    const selectedSet = new Set(selectedValues);
    const mappingCopy = { ...columnMapping };

    Object.keys(mappingCopy).forEach((key) => {
      if (mappingCopy[key].type === selectorName && !selectedSet.has(key)) {
        mappingCopy[key].type = "feature";
      }
    });

    selectedValues.forEach((key) => {
      mappingCopy[key].type = selectorName;
    });

    setColumnMapping(mappingCopy);
  };

  const handleProcessSubmit = () => {
    setAlertSettings({
      isVisible: false,
      type: "error",
      message: "",
    });

    // set snackbar notification, dependent on post status
    const snackbar = { ...snackbarSettings };

    postData(
      `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasets[currentDatasetIdx].datasetName}/experiment`,
      {
        experimentName,
        experimentType,
        columnMapping,
        sameDistributionTargets,
        splitFile:
          splitMethod === "custom" && selectedSplitFile !== "default"
            ? selectedSplitFile
            : null,
        setMakerPeriodLength: parseInt(200 / (validationRatio*2), 10),
        setMakerValidationIteration: parseInt(ValSplitIndex, 10)
      },
    ).then((data) => {
      // set snackbar notification, dependent on post status
      snackbar.message = data.data;
      if (data.status === 201) {
        setErrors({});
        snackbar.isOpen = true;
        snackbar.type = "success";
        snackbar.message = t(
          "experimentConfigurator.SuccessExperimentProcessingStarted",
        );

        const newExperiment = {
          experimentName,
          experimentType,
        };
        setExperiments((prev) => [newExperiment, ...prev]);
        setCurrentExperimentIdx(0);

        postData(
          `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasets[currentDatasetIdx].datasetName}/experiment/${experimentName}/processing`,
          {},
        ).then((experimentData) => {
          if (experimentData.status === 200) {
            setProcessingStarted(true);
          } else {
            snackbar.type = "error";
          }
        });
      } else {
        snackbar.type = "error";
        if (data.status === 422) {
          setErrors(data.detail);
          setAlertSettings({
            isVisible: true,
            type: "error",
            message: t("experimentErrors.FailedCreationError"),
          });
        }
        if (data.status === 409) {
          // write error details to form
          setErrors(data.detail);

          snackbar.message = t(
            "experimentConfigurator.ErrorExperimentAlreadyExists",
          );
        }
      }
    });

    setSnackbarSettings(snackbar);
  };

  const handleExperimentNameChange = (event) => {
    if (errors.hasOwnProperty("experimentName")) {
      const errorsCopy = { ...errors };
      delete errorsCopy.experimentName;
      setErrors(errorsCopy);
    }
    setExperimentName(event.target.value);
  };

  const handleSelectorChange = (selectorName) => (event) => {
    // reset error message
    if (errors.hasOwnProperty(selectorName)) {
      const errorsCopy = { ...errors };
      delete errorsCopy[selectorName];
      setErrors(errorsCopy);
    }

    const mappingCopy = { ...columnMapping };
    // check if old value exists, if so, reset it as feature
    if (getKeysArray(selectorName).length === 1) {
      mappingCopy[getKeysArray(selectorName)[0]].type = "feature";
    }
    // set selected item as type of the selector e.g. "timestamp"
    if (event.target.value !== "None") {
      mappingCopy[event.target.value].type = selectorName;
    }
    setColumnMapping(mappingCopy);
  };

  const handleMultiSelectorChange = (selectorName) => (event) => {
    // reset error message
    if (errors.hasOwnProperty(selectorName)) {
      const errorsCopy = { ...errors };
      delete errorsCopy[selectorName];
      setErrors(errorsCopy);
    }
    const { value } = event.target;
    const mappingCopy = { ...columnMapping };

    if (value.includes("Select All")) {
      // check if all are already Selected or not, if yes then unselect all
      if (
        Object.values(columnMapping).filter(
          (column) => column.type === "ignored",
        ).length === Object.values(columnMapping).length
      ) {
        Object.values(columnMapping).map((column) => {
          column.type = "feature";
        });
      } else {
        Object.values(columnMapping).map((column) => {
          column.type = selectorName;
        });
      }
    } else {
      // check if an Item was removed or added
      const removedEntries = getKeysArray(selectorName).filter(
        (x) => event.target.value.indexOf(x) === -1,
      );
      if (removedEntries.length === 0) {
        mappingCopy[event.target.value.at(-1)].type = selectorName;
      } else {
        mappingCopy[removedEntries.at(-1)].type = "feature";
      }
    }
    setColumnMapping(mappingCopy);
  };

  const handleValSplitIndexChange = (event) => {
    const { value } = event.target;
    if (value === "" || /^\d+$/.test(value)) {
      setValSplitIndex(value);
    }
  };

  const handleSplitMethodChange = (event) => {
    const nextMethod = event.target.value;
    setSplitMethod(nextMethod);
  };

  const clientValidate = useCallback(() => {
    const nextErrors = {};

    if (experimentType === "Forecast") {
      const tsCols = getKeysArray("timestamp");
      if (!tsCols.length) {
        nextErrors.timestamp = { message: "TimestampRequiredForForecast" };
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [experimentType, getKeysArray]);

  useEffect(() => {
    if (datasets.length >= 1) {
      fetchDataset();
    }
    setExperimentName(
      t("experimentTypeSelector.ExperimentNameStart") +
        t(`experimentTypeSelector.${experimentType}Title`) +
        t("experimentTypeSelector.ExperimentNameEnd"),
    );
  }, [datasets, currentDatasetIdx]);

  useEffect(() => {
    if (datasets.length >= 1) {
      fetchSplitInfo();
    }
  }, [datasets, currentDatasetIdx]);

  useEffect(() => {}, [alertSettings]);

  let targetSelect = null;
  if (experimentType === "Classification") {
    targetSelect = (
      <Select
        native
        value={getKeysArray("target")[0] ?? ""}
        onChange={handleSelectorChange("target")}
        input={<OutlinedInput id="select-target-cols" />}
        sx={{
          width: "100%",
          mt: 1,
          "& select": {
            padding: theme.spacing(1.25),
            borderRadius: 1,
            outline: "none",
          },
          "& option": {
            padding: "8px 10px",
          },
        }}
      >
        <option aria-label="None" value="">
          None
        </option>
        {columnNames.map((name) => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </Select>
    );
  } else if (useTargetListbox) {
    targetSelect = (
      <Select
        native
        multiple
        value={getKeysArray("target")}
        onChange={handleNativeMultiSelectorChange("target")}
        input={<OutlinedInput id="select-target-cols" />}
        sx={{
          width: "100%",
          mt: 1,
          "& select": {
            minHeight: 220,
            padding: theme.spacing(1.25),
            borderRadius: 1,
            outline: "none",
          },
          "& option": {
            padding: "8px 10px",
          },
        }}
      >
        {columnNames.map((name) => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </Select>
    );
  } else {
    targetSelect = (
      <Select
        multiple
        value={getKeysArray("target")}
        label={t("experimentConfigurator.TargetDropdown")}
        onChange={handleMultiSelectorChange("target")}
        input={
          <OutlinedInput
            id="select-target-cols"
            label={t("experimentConfigurator.TargetDropdown")}
          />
        }
        renderValue={(selected) => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {selected.map((value) => (
              <Chip key={`target-${value}`} label={value} />
            ))}
          </Box>
        )}
        MenuProps={theme.multiSelectMenuProps}
        sx={{ minWidth: 200, maxWidth: 520, mt: 1 }}
      >
        <MenuItem
          value="Select All"
          sx={{ fontWeight: theme.typography.fontWeightMedium }}
        >
          <b>Select All</b>
        </MenuItem>
        {columnNames.map((name) => (
          <MenuItem
            key={name}
            value={name}
            style={getSelectorStyles(name, columnNames, theme)}
          >
            {name}
          </MenuItem>
        ))}
      </Select>
    );
  }

  let ignoredSelect = null;
  if (useIgnoredListbox) {
    ignoredSelect = (
      <Select
        native
        multiple
        value={getKeysArray("ignored")}
        onChange={handleNativeMultiSelectorChange("ignored")}
        input={<OutlinedInput id="select-ignore-cols" />}
        sx={{
          width: "100%",
          mt: 1,
          "& select": {
            minHeight: 220,
            padding: theme.spacing(1.25),
            borderRadius: 1,
            outline: "none",
          },
          "& option": {
            padding: "8px 10px",
          },
        }}
      >
        {columnNames.map((name) => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </Select>
    );
  } else {
    ignoredSelect = (
      <Select
        multiple
        value={getKeysArray("ignored")}
        label="Ignored Column(s)"
        onChange={handleMultiSelectorChange("ignored")}
        input={<OutlinedInput id="select-ingore-cols" label="Chip" />}
        renderValue={(selected) => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {selected.map((value) => (
              <Chip key={`ignored-${value}`} label={value} />
            ))}
          </Box>
        )}
        MenuProps={theme.multiSelectMenuProps}
        sx={{ minWidth: 200, maxWidth: 520 }}
      >
        <MenuItem
          value="Select All"
          sx={{ fontWeight: theme.typography.fontWeightMedium }}
        >
          <b>Select All</b>
        </MenuItem>
        {columnNames.map((name) => (
          <MenuItem
            key={name}
            value={name}
            style={getSelectorStyles(name, columnNames, theme)}
          >
            {name}
          </MenuItem>
        ))}
      </Select>
    );
  }

  const splitMethodHelperText = (() => {
    if (!splitMethod) {
      return t("experimentConfigurator.SplitMethodPlaceholderHelper");
    }
    if (splitMethod === "custom") {
      return t("experimentConfigurator.SplitMethodCustomHelper");
    }
    return t("experimentConfigurator.SplitMethodKILabHelper");
  })();

  const splitPreviewBars = useMemo(() => {
    if (splitMethod !== "kilab") {
      return [];
    }
    const totalBars = 200;
    const ratio = Number(validationRatio) / 100 || 0;
    const iterationsNum = Math.max(0, parseInt(ValSplitIndex, 10) || 0);
    const valCount = Math.max(0, Math.round(totalBars * ratio));
    const step = valCount > 0 ? Math.ceil(totalBars / valCount) : totalBars + 1;
    const offset = step > 0 ? iterationsNum % step : 0;
    const bars = Array.from({ length: totalBars }, () => "train");
    let placed = 0;
    for (
      let i = offset;
      i < totalBars && placed < valCount;
      i += Math.max(1, step)
    ) {
      bars[i] = "val";
      placed += 1;
    }
    return bars;
  }, [splitMethod, validationRatio, ValSplitIndex]);

  return datasets.length >= 1 ? (
    <>
      <Snackbar open={snackbarSettings.isOpen} autoHideDuration={8000}>
        <Alert severity={snackbarSettings.type}>
          {snackbarSettings.message}
        </Alert>
      </Snackbar>
      {/* eslint-disable-next-line no-nested-ternary */}
        {processingStarted ? (
                // if processing was already initialized,show status
                <ExperimentProcessingStatus experimentName={experimentName}/>
        ) : (
        experimentType === "Error Model" ? (
                    <ExperimentConfiguratorErrorModel/>
        ) : (
        // else render experiment configuration
        <form ref={formRef}>
          <Grid
            id="experiment-configurator-container"
            container
            spacing={2}
            sx={{ padding: theme.spacing(4, 1) }}
          >
            <Grid item lg={3} xs={12}>
              <Typography variant="h4">
                {t("experimentConfigurator.PageTitle_1")} {experimentType}{" "}
                {t("experimentConfigurator.PageTitle_2")}
                <em>
                  {datasets.length
                    ? datasets[currentDatasetIdx].datasetName
                    : ""}
                </em>
              </Typography>
              <br />
              <Grid container rowSpacing={3}>
                {/* -----------------------------------------Target Setting------------------------------------------------------ */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Typography variant="h6">
                      {t("experimentConfigurator.TargetTitle")}
                    </Typography>
                    <Tooltip title={t("experimentConfigurator.HintTarget")}>
                      <IconButton>
                        <InfoIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {experimentType === "Classification" ? (
                    <Typography variant="body2">
                      {t(
                        "experimentConfigurator.TargetDescriptionClassification",
                      )}
                    </Typography>
                  ) : (
                    <Typography variant="body2">
                      {t("experimentConfigurator.TargetDescription")}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <FormControl
                    required
                    error={Boolean(errors.target)}
                    fullWidth
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 0.5,
                        mb: 1,
                      }}
                    >
                      <InputLabel shrink htmlFor="select-target-cols">
                        {t("experimentConfigurator.TargetDropdown")}
                      </InputLabel>
                    </Box>

                    {experimentType !== "Classification" &&
                      useTargetListbox &&
                      getKeysArray("target").length > 0 && (
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.1,
                            alignItems: "center",
                            justifyContent: "space-between",
                            borderRadius: 1,
                            border: `1px solid ${theme.palette.divider}`,
                            px: 1,
                            py: 0.5,
                            mb: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.5,
                            }}
                          >
                            {getKeysArray("target")
                              .slice(0, 6)
                              .map((val) => (
                                <Chip
                                  key={`target-chip-${val}`}
                                  size="small"
                                  label={val}
                                />
                              ))}
                            {getKeysArray("target").length > 6 && (
                              <Chip
                                size="small"
                                label={`+${
                                  getKeysArray("target").length - 6
                                } more`}
                              />
                            )}
                          </Box>
                          <Button
                            size="small"
                            onClick={() => {
                              const mappingCopy = { ...columnMapping };
                              getKeysArray("target").forEach((k) => {
                                mappingCopy[k].type = "feature";
                              });
                              setColumnMapping(mappingCopy);
                            }}
                          >
                            {t("common.clear")}
                          </Button>
                        </Box>
                      )}
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}
                    >
                      <Box sx={{ flex: 2 }}>
                        {targetSelect}

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 0.1,
                          }}
                        >
                          <FormHelperText>{targetHelperText}</FormHelperText>
                          {experimentType !== "Classification" && (
                            <ViewToggle
                              checked={useTargetListbox}
                              onChange={() =>
                                setUseTargetListbox((prev) => !prev)
                              }
                              checkedLabel={t(
                                "experimentConfigurator.MultiSelector",
                              )}
                              uncheckedLabel={t(
                                "experimentConfigurator.OriginalSelector",
                              )}
                              tooltip={t(
                                "experimentConfigurator.TargetSelectorHint",
                              )}
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </FormControl>
                </Grid>

                {/* -----------------------------Checkbox shown only if multiple targets are selected-------------------------------- */}
                {getKeysArray("target").length > 1 && (
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={sameDistributionTargets}
                          onChange={(e) =>
                            setSameDistributionTargets(e.target.checked)
                          }
                        />
                      }
                      label={t(
                        "experimentConfigurator.CheckboxSameDistributionLabel",
                      )}
                    />
                  </Grid>
                )}
                {/* -----------------------------------------Feature Setting------------------------------------------------------ */}
                <Grid item xs={12}>
                  <Typography variant="h6">
                    {t("experimentConfigurator.FeatureTitle")}
                  </Typography>
                  <Typography variant="body2">
                    {t("experimentConfigurator.FeatureDescription")}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <ViewToggle
                      checked={useIgnoredListbox}
                      onChange={() => setUseIgnoredListbox((prev) => !prev)}
                      checkedLabel={t("experimentConfigurator.MultiSelector")}
                      uncheckedLabel={t(
                        "experimentConfigurator.OriginalSelector",
                      )}
                      tooltip={t(
                        "experimentConfigurator.IgnoreColumnSelectorHint",
                      )}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                    }}
                  >
                    <FormControl fullWidth error={Boolean(errors.ignored)}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <InputLabel>
                          {t("experimentConfigurator.IgnoreDropdown")}
                        </InputLabel>
                      </Box>
                      {useIgnoredListbox &&
                        getKeysArray("ignored").length > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.5,
                              alignItems: "center",
                              justifyContent: "space-between",
                              borderRadius: 1,
                              border: `1px solid ${theme.palette.divider}`,
                              px: 1,
                              py: 0.5,
                              mb: 1,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {getKeysArray("ignored")
                                .slice(0, 6)
                                .map((val) => (
                                  <Chip
                                    key={`ignored-chip-${val}`}
                                    size="small"
                                    label={val}
                                  />
                                ))}
                              {getKeysArray("ignored").length > 6 && (
                                <Chip
                                  size="small"
                                  label={`+${
                                    getKeysArray("ignored").length - 6
                                  } more`}
                                />
                              )}
                            </Box>
                            <Button
                              size="small"
                              onClick={() => {
                                const mappingCopy = { ...columnMapping };
                                getKeysArray("ignored").forEach((k) => {
                                  mappingCopy[k].type = "feature";
                                });
                                setColumnMapping(mappingCopy);
                              }}
                            >
                              {t("common.clear")}
                            </Button>
                          </Box>
                        )}
                      {ignoredSelect}
                      <FormHelperText>
                        {t(
                          `experimentConfigurator.Error ${errors.ignored?.message}`,
                        )}
                      </FormHelperText>
                    </FormControl>
                    <Tooltip title={t("experimentConfigurator.HintIgnore")}>
                      <IconButton>
                        <InfoIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <FormControl error={Boolean(errors.id)}>
                    <InputLabel>
                      {t("experimentConfigurator.IdDropdown")}
                    </InputLabel>
                    <Select
                      value={
                        getKeysArray("id").length > 0
                          ? getKeysArray("id")
                          : ["None"]
                      }
                      onChange={handleSelectorChange("id")}
                      label="ID Column"
                      sx={{ minWidth: 200 }}
                    >
                      <MenuItem value="None">
                        <em>{t("experimentConfigurator.IdDropdown")}</em>
                      </MenuItem>
                      {columnNames.map((name) => (
                        <MenuItem
                          key={`id-${name}`}
                          value={name}
                          style={getSelectorStyles(name, columnNames, theme)}
                        >
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {t(`experimentConfigurator.Error ${errors.id?.message}`)}
                    </FormHelperText>
                  </FormControl>
                  <Tooltip title={t("experimentConfigurator.HintID")}>
                    <IconButton>
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                </Grid>
                <Grid item xs={12}>
                  <FormControl
                    required={experimentType === "Forecast"}
                    error={Boolean(errors.timestamp)}
                  >
                    <InputLabel>
                      {t("experimentConfigurator.TimestampDropdown")}
                    </InputLabel>
                    <Select
                      // set None as default
                      value={getKeysArray("timestamp")[0] ?? "None"}
                      onChange={handleSelectorChange("timestamp")}
                      label="Timestamp Column"
                      sx={{ minWidth: 200 }}
                    >
                      <MenuItem value="None">
                        <em>{t("experimentConfigurator.NoTimestamp")}</em>
                      </MenuItem>
                      {columnNames.map((name) => (
                        <MenuItem
                          key={`timestamp-${name}`}
                          value={name}
                          style={getSelectorStyles(name, columnNames, theme)}
                        >
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {errors.timestamp?.message &&
                        t(`experimentConfigurator.${errors.timestamp.message}`)}
                      {!errors.timestamp?.message &&
                        experimentType === "Forecast" &&
                        t(
                          "experimentConfigurator.TimestampRequiredForForecast",
                        )}
                    </FormHelperText>
                  </FormControl>
                  <Tooltip title={t("experimentConfigurator.HintTime")}>
                    <IconButton>
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>

              {alertSettings.isVisible && (
                <Alert severity={alertSettings.type} sx={{ marginTop: "10px" }}>
                  <AlertTitle>{alertSettings.message}</AlertTitle>
                  {errors && (
                    <div>
                      {Object.keys(errors).map((error) => (
                        <div key={error}>
                          {" "}
                          - {t(`experimentErrors.${error}`, errors[error])}
                        </div>
                      ))}
                    </div>
                  )}
                </Alert>
              )}
            </Grid>
            {/* -------------------------------------Experiment Preview----------------------------------------- */}
            <Grid item lg={9} xs={12}>
              <ExperimentPreview
                columnMapping={columnMapping}
                baseData={baseData}
                getKeysArray={getKeysArray}
              />
            </Grid>
            <Grid item lg={9} xs={6} />
            {/* ------------------------------------Split Data File ---------------------------------------------------*/}
            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box>
                    <Typography variant="h6">
                      {t("experimentConfigurator.SplitDataTitle")}
                    </Typography>
                    <Typography variant="body2">
                      {t("experimentConfigurator.SplitDataDescription")}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <FormControl fullWidth>
                      <InputLabel>
                        {t("experimentConfigurator.SplitMethodDropdown")}
                      </InputLabel>
                      <Select
                        value={splitMethod}
                        onChange={handleSplitMethodChange}
                        label={t("experimentConfigurator.SplitMethodDropdown")}
                        sx={{ minWidth: 200 }}
                      >
                        <MenuItem value="" disabled>
                          <em>
                            {t("experimentConfigurator.SplitMethodPlaceholder")}
                          </em>
                        </MenuItem>
                        <MenuItem value="kilab">
                          {t("experimentConfigurator.SplitMethodKILab")}
                        </MenuItem>
                        <MenuItem value="custom">
                          {t("experimentConfigurator.SplitMethodCustom")}
                        </MenuItem>
                      </Select>
                      <FormHelperText>{splitMethodHelperText}</FormHelperText>
                    </FormControl>
                    {splitMethod === "custom" && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        <FormControl fullWidth>
                          <InputLabel>
                            {t("experimentConfigurator.SplitFileDropdown")}
                          </InputLabel>
                          <Select
                            value={selectedSplitFile}
                            onChange={(e) =>
                              setSelectedSplitFile(e.target.value)
                            }
                            label={t(
                              "experimentConfigurator.SplitFileDropdown",
                            )}
                          >
                            <MenuItem value="default">
                              <em>
                                {t("experimentConfigurator.UseDefaultSplit")}
                              </em>
                            </MenuItem>
                            {splitFiles.map((file) => (
                              <MenuItem key={file} value={file}>
                                {file}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            {selectedSplitFile === "default"
                              ? t(
                                  "experimentConfigurator.DefaultSplitHelperText",
                                )
                              : t(
                                  "experimentConfigurator.CustomSplitHelperText",
                                )}
                          </FormHelperText>
                        </FormControl>
                        <Tooltip
                          title={t("experimentConfigurator.AddSplitDataButton")}
                        >
                          <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={() =>
                              navigate(
                                `/split-data/${datasets[currentDatasetIdx].datasetName}`,
                              )
                            }
                            sx={{ alignSelf: "flex-start" }}
                          >
                            {t("experimentConfigurator.AddSplit")}
                          </Button>
                        </Tooltip>
                      </Box>
                    )}
                    {splitMethod === "kilab" && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        <FormControl fullWidth>
                          <InputLabel>
                            {t("experimentConfigurator.ValidationRatioLabel")}
                          </InputLabel>
                          <Select
                            value={validationRatio}
                            onChange={(e) => setValidationRatio(e.target.value)}
                            label={t(
                              "experimentConfigurator.ValidationRatioLabel",
                            )}
                          >
                            <MenuItem value="14">~14%</MenuItem>
                            <MenuItem value="17">~17%</MenuItem>
                            <MenuItem value="20">~20%</MenuItem>
                          </Select>
                          <FormHelperText>
                            {t("experimentConfigurator.ValidationRatioHelper")}
                          </FormHelperText>
                        </FormControl>
                        <TextField
                          fullWidth
                          label={t(
                            "experimentConfigurator.ValSplitIndexLabel",
                          )}
                          value={ValSplitIndex}
                          onChange={handleValSplitIndexChange}
                          type="number"
                          inputProps={{ min: 0, step: 1 }}
                          helperText={t(
                            "experimentConfigurator.ValSplitIndexHelper",
                          )}
                        />
                      </Box>
                    )}
                    {splitMethod === "kilab" && (
                      <Box sx={{ width: "100%" }}>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                          {t("experimentConfigurator.SplitPreviewTitle")}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          {t("experimentConfigurator.SplitPreviewDescription")}
                        </Typography>
                        <Box
                          sx={{
                            borderRadius: 1,
                            border: `1px solid ${theme.palette.divider}`,
                            p: 2,
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "repeat(200, 1fr)",
                              gap: "2px",
                              height: 96,
                            }}
                          >
                            {splitPreviewBars.map((bar, idx) => (
                              <Box
                                key={`split-preview-${idx}`}
                                sx={{
                                  backgroundColor:
                                    bar === "val"
                                      ? theme.palette.warning.main
                                      : theme.palette.primary.main,
                                  borderRadius: "1px",
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 3,
                            flexWrap: "wrap",
                            mt: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: "2px",
                                backgroundColor: theme.palette.primary.main,
                              }}
                            />
                            <Typography variant="caption">
                              {t("experimentConfigurator.SplitLegendTrain")}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: "2px",
                                backgroundColor: theme.palette.warning.main,
                              }}
                            />
                            <Typography variant="caption">
                              {t(
                                "experimentConfigurator.SplitLegendValidation",
                              )}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Grid>
            {/* -----------------------------------------Experiment Setting------------------------------------------------------ */}
            <Grid item xs={12}>
              <Typography variant="h6">
                {t("experimentConfigurator.ExperimentSettingsTitle")}
              </Typography>
              <Typography variant="body2">
                {t("experimentConfigurator.ExperimentSettingsDescription")}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControl>
                <TextField
                  label={t("experimentConfigurator.ExperimentNameFillable")}
                  value={`${experimentName}`}
                  onChange={handleExperimentNameChange}
                  error={Boolean(errors.experimentName)}
                  helperText={t(
                    `experimentConfigurator.Error ${errors.experimentName?.message}`,
                  )}
                />
              </FormControl>
              <Tooltip title={t("experimentConfigurator.HintExperimentName")}>
                <IconButton>
                  <InfoIcon />
                </IconButton>
              </Tooltip>
            </Grid>
            {/* ----------------------------------------- Process Button------------------------------------------------------ */}
            <Grid item lg={3} xs={6} sx={{ alignSelf: "end", mt: 3 }}>
              <Button
                color="primary"
                size="medium"
                variant="contained"
                onClick={() => {
                  const formValid = formRef.current.reportValidity();
                  const clientValid = clientValidate();

                                    if (formValid && clientValid) {
                                        handleProcessSubmit();
                                    }
                                }}
                            >
                                {t("experimentConfigurator.SaveAndProcessButton")}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            ))}
        </>
    ) : (
        // if user lands on this page, without having at least one dataset, show this hint
        <Grid item xs={3} sx={{margin: theme.spacing(6)}}>
            <Typography variant="body">
                {t("experimentConfigurator.HintNoDatasetYet")}
            </Typography>
        </Grid>
    );
}
